import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { XCircle, X } from "lucide-react";
import "./ErrorModal.css";

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  buttonText?: string;
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  title = "Error",
  message = "Something went wrong. Please try again.",
  buttonText = "Close",
}) => {
  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="error-modal-backdrop"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="error-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="error-modal-title"
          >
            {/* Close Button */}
            <button
              className="error-modal__close"
              onClick={onClose}
              aria-label="Close modal"
            >
              <X size={20} />
            </button>

            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
              className="error-modal__icon-wrapper"
            >
              <XCircle className="error-modal__icon" />
            </motion.div>

            {/* Content */}
            <div className="error-modal__content">
              <h2 id="error-modal-title" className="error-modal__title">
                {title}
              </h2>
              <p className="error-modal__message">{message}</p>
            </div>

            {/* Action Button */}
            <button className="error-modal__button" onClick={onClose}>
              {buttonText}
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default ErrorModal;
